import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { COOKIE_KEYS, getCookies } from "@/helpers/cookie";
import { categoriesAtom } from "@/store/global";
import { userAtom, type User } from "@/store/user";
import { useAtom } from "jotai";
import { isEqual, omit } from "lodash-es";
import { syncCategoriesMutation } from "@/queries/syncCategoriesMutationOptions";

declare global {
  interface Window {
    posthog: any;
  }
}

const fetchUser = async () => {
  const response = await axios.get<User>("/me.json");
  return response.data;
};

export const useMe = () => {
  const [, setUser] = useAtom(userAtom);
  const [categories, setCategories] = useAtom(categoriesAtom);

  const cookieLoggedIn = getCookies().get(COOKIE_KEYS.LOGGED_IN) === "1";
  const [fetchEnabled, setFetchEnabled] = useState(cookieLoggedIn);
  const mutation = syncCategoriesMutation(categories);

  const { data: user, isSuccess } = useQuery({
    queryKey: ["me"],
    queryFn: fetchUser,
    enabled: fetchEnabled,
  });

  useEffect(() => {
    if (user) {
      setUser(user);
    }

    /**
     * Utilize cookieLoggedIn to ensure that fetchEnabled is set to true when the app is fully loaded
     * and the cookie indicates a logged-in state, even if user.email is initially empty.
     * This prevents premature fetching before the app has completed its initialization.
     */
    setFetchEnabled(cookieLoggedIn || !!user?.email);

    if (user?.email) {
      window.posthog?.identify?.(user.email, omit(user, ["categories"]));
    }

    if (user?.categories) {
      const combinedCategories = [
        ...user.categories,
        ...categories.filter((category) => !user.categories?.find((c) => c.value === category.value)),
      ];

      setCategories(combinedCategories);

      const isCategoriesUpdated = !isEqual(categories, user.categories);

      if (isCategoriesUpdated) {
        mutation.mutate();
      }
    }
  }, [isSuccess]);
};
